//@ts-nocheck
import React, {useEffect, useState} from "react";
import {ourToast} from "../atoms/Toast";
import Page500 from "../pages/Page500";

type EnvFetcherType = {
  children: JSX.Element,
  onLoad: (val: {
    backend_url: string,
    environment_name: string,
    release_name: string,
    sentry_url?: string,
    sentry_dsn?: string,
  }) => void
}

export function EnvFetcher({children, onLoad}: EnvFetcherType) {
  const [env, setEnv] = useState<string | null>(null);

  useEffect(() => {
    fetch("/configs/env.json")
      .then((resp) => resp.json())
      .then((body) => {
        let envJsonString = JSON.stringify(body);
        if (!envJsonString) {
          return <Page500/>
        } else {
          localStorage.setItem('env', envJsonString)
          setEnv(envJsonString)
          if (onLoad) {
            onLoad(body);
          }
        }
      })
  }, [])

  if (env) {
    return children
  }
  return null;
}

export function getOnixUrl(path: string) {
  const env = localStorage.getItem("env")
  if (env != null) {
    return JSON.parse(env)?.backend_url.replace(/\/+$/, '') + path;
  } else {
    return null
  }
}

function makeRequest<R>(method: 'get' | 'post' | 'put' | 'patch' | 'delete', url: string, data?: any): Promise<R> {
  const backendUrl = getOnixUrl(url);
  return new Promise((resolve, reject) => {
    if (backendUrl == null) {
      reject(new Error("No backend url found"));
      return;
    }

    const headers: Record<string, string> = {
      'Authorization': "JWT " + localStorage.getItem('jwtKey'),
      'Accept': 'application/json',
    };

    let fetchUrl = backendUrl;
    let body: string | FormData | undefined;

    if (method === 'post' || method === 'put' || method === 'patch') {
      if (data instanceof FormData) {
        // Let the browser set Content-Type with the correct multipart boundary
        body = data;
      } else {
        headers['Content-Type'] = 'application/json';
        body = data !== undefined ? JSON.stringify(data) : undefined;
      }
    } else if (data) {
      const params = new URLSearchParams();
      for (const key of Object.keys(data)) {
        if (data[key] !== undefined && data[key] !== null) {
          params.append(key, data[key]);
        }
      }
      const queryString = params.toString();
      if (queryString) {
        fetchUrl = backendUrl + '?' + queryString;
      }
    }

    fetch(fetchUrl, {method: method.toUpperCase(), headers, body})
      .then((response) => response.json())
      .then((json) => resolve(json))
      .catch((err) => reject(new Error(err.message)));
  });
}

const HttpService = {
  GET: function <R>(url: string, query?: Record<string, any>): Promise<R> {
    return makeRequest<R>('get', url, query);
  },
  POST: function <P, R>(url: string, data: P): Promise<R> {
    return makeRequest<R>('post', url, data);
  },
  PUT: function <P, R>(url: string, data: P): Promise<R> {
    return makeRequest<R>('put', url, data);
  },
  PATCH: function <P, R>(url: string, data: P): Promise<R> {
    return makeRequest<R>('patch', url, data);
  },
  DELETE: function <R>(url: string, query?: Record<string, any>): Promise<R> {
    return makeRequest<R>('delete', url, query);
  },
};
export { HttpService };

export function multiPartHttpRequest<P, F, R, E = Error[]>(url: string, fields: P, files: F, onResponse: (res: R) => void, onError: (err: E) => void, method = 'post') {
  const formData = new FormData();

  for (const fileFieldName of Object.keys(files)) {
    const fileValue = files[fileFieldName];
    if (Array.isArray(fileValue)) {
      for (const file of fileValue) {
        formData.append(fileFieldName, file);
      }
    } else {
      formData.append(fileFieldName, fileValue);
    }
  }
  for (const fieldName of Object.keys(fields)) {
    const fieldValue = fields[fieldName];
    if (fieldValue instanceof File) {
      formData.append(fieldName, fieldValue);
    } else if (Array.isArray(fieldValue) && fieldValue.length > 0 && fieldValue[0] instanceof File) {
      for (const file of fieldValue) {
        formData.append(fieldName, file);
      }
    } else {
      formData.append(fieldName, fieldValue || '');
    }
  }

  fetch(getOnixUrl(url), {
    method: method.toUpperCase(),
    headers: {
      'Authorization': "JWT " + localStorage.getItem('jwtKey'),
    },
    body: formData,
  })
    .then((response) => {
      return response.json().then((data) => {
        if (!response.ok) {
          if (data.detail) {
            onError([{message: data.detail}]);
          } else if (data.message) {
            onError([{message: data.message}]);
          } else if (Array.isArray(data) && data.length > 0 && data[0].message) {
            onError(data);
          } else {
            onError([{message: JSON.stringify(data)}]);
          }
        } else {
          onResponse(data);
        }
      });
    })
    .catch((err) => {
      let errorBody;
      try {
        errorBody = JSON.parse(err.message);
      } catch (e) {
        onError([{message: '' + err}]);
        return;
      }
      if (errorBody.detail) {
        onError([{message: errorBody.detail}]);
      } else if (errorBody.message) {
        onError([{message: errorBody.message}]);
      } else {
        onError([{message: errorBody}]);
      }
    });
}

export function downloadFile(sUrl) {

  let isChrome = navigator.userAgent.toLowerCase().indexOf('chrome') > -1;
  let isSafari = navigator.userAgent.toLowerCase().indexOf('safari') > -1;

  //iOS devices do not support downloading. We have to inform user about this.
  if (/(iP)/g.test(navigator.userAgent)) {
    alert('Your device does not support files downloading. Please try again in desktop browser.');
    return false;
  }

  //If in Chrome or Safari - download via virtual link click
  if (isChrome || isSafari) {
    //Creating new link node.
    let link = document.createElement('a');
    link.href = sUrl;

    if (link.download !== undefined) {
      //Set HTML5 download attribute. This will prevent file from opening if supported.
      link.download = sUrl.substring(sUrl.lastIndexOf('/') + 1, sUrl.length);
      link.setAttribute("target", "_blank")
    }

    if (document.createEvent) {
      link.dispatchEvent(new MouseEvent('click'));
      return true;
    }
  }

  // Force file download (whether supported by server).
  if (sUrl.indexOf('?') === -1) {
    sUrl += '?download';
  }

  window.open(sUrl, '_self');
  return true;
}

export function makeOutputFile({
                                 brand, retailer, allSelected, ignoredUnifiedProducts, unifiedProductsIds,
                                 distributeDataTemplateId, downloadImages, search, collection, orderConfirmationBatch,
                                 onFileCreated, onError
                               }) {
  let urlBase = getOnixUrl("/")

  const postData = {
    useOrderConfirmationBatch: orderConfirmationBatch ? orderConfirmationBatch.id : null,
    allSelected: allSelected,
    ignoredUnifiedProducts: ignoredUnifiedProducts,
    unifiedProductsIds: unifiedProductsIds,
    distributeDataTemplateId: distributeDataTemplateId,
    downloadImages: downloadImages,
    filter: {
      name: search,
      collection: collection,
    }
  }

  if (brand) {
    postData.brand = brand
  } else {
    postData.retailer = retailer
  }

  fetch(urlBase + 'generateDistributeDataOutput/', {
    method: 'POST',
    headers: {
      'Authorization': "JWT " + localStorage.getItem('jwtKey'),
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(postData),
  })
    .then((response) => response.json())
    .then((body) => {
      if (body) {
        onFileCreated(body.generateDistributeDataOutput.instance);
      } else {
        onError(body);
        ourToast('error', 'Oops something went wrong!');
      }
    })
    .catch((err) => {
      onError('' + err);
      ourToast('error', 'Oops something went wrong!');
    });
}


export function packOrderConfirmationFiles(files) {
  let fileFields = {}
  for (let orderConfirmName of Object.keys(files)) {
    for (let index = 0; index < files[orderConfirmName].length; index++) {
      fileFields[orderConfirmName + '_' + index] = files[orderConfirmName][index];
    }
  }
  return fileFields
}
